import { LocationData, PowerGrid } from '../types';
import { SERVER_TYPES, CHILLER_TYPES } from '../constants';

// Helper function to calculate gross power capacity for a location
// This represents the total potential power capacity from the infrastructure
export const calculateLocationGrossPower = (
  locationData: LocationData | null | undefined,
  allPowerGrids: Record<string, PowerGrid>
): number => {
  if (!locationData || !locationData.powerGridId) {
    return 0;
  }

  const grid = allPowerGrids[locationData.powerGridId];
  if (!grid || !grid.suppliers.length) {
    return 0;
  }

  // Use the selected supplier if one is specified, otherwise no power is available
  let supplier;
  if (locationData.selectedPowerSupplierId) {
    supplier = grid.suppliers.find(s => s.id === locationData.selectedPowerSupplierId);
  }
  
  // If no supplier is selected or the selected supplier wasn't found, no power is available
  if (!supplier) {
    return 0; // No power supplier selected means no power available
  }

  // Check if we have a transformer and it's properly configured
  if (!locationData.transformer?.connected || !locationData.transformer?.capacityKW) {
    return 0;
  }

  // Power flow: Supplier -> Transformer -> Powerhouse -> UPS -> Racks
  let power = Math.min(supplier.maxOutput, locationData.transformer.capacityKW);
  power *= locationData.transformer.efficiency;

  // Check powerhouse
  if (!locationData.powerhouse?.installed || !locationData.powerhouse?.capacityKW) {
    // If no powerhouse or it's not properly configured, no power is delivered
    return 0;
  }
  power = Math.min(power, locationData.powerhouse.capacityKW);
  power *= locationData.powerhouse.efficiency;

  // Check UPS
  if (locationData.ups?.installed && locationData.ups?.maxOutputKW) {
    // UPS doesn't limit continuous power, but has its own efficiency loss
    // We'll model this as a slight loss on the way through.
    // The main UPS function is backup, which is handled elsewhere.
    power = Math.min(power, locationData.ups.maxOutputKW);
    power *= locationData.ups.efficiency;
  }

  return power;
};

// The calculateLocationAvailablePower alias has been removed.
// All code now uses either calculateLocationGrossPower or location.availablePowerKW directly.

/**
 * Calculates PUE (Power Usage Effectiveness) for a single location.
 * PUE = total facility power / IT equipment power.
 * Returns 1.0 when there is no IT load (perfect, no overhead).
 */
export const calculateLocationPUE = (location: LocationData): number => {
  let itPowerKW = 0;
  let overheadPowerKW = 0;

  if (location.floors) {
    Object.values(location.floors).forEach(floor => {
      floor.racks?.forEach(rack => {
        rack.servers?.forEach(server => {
          const st = SERVER_TYPES.find(s => s.id === server.type);
          if (st) itPowerKW += st.powerDraw;
        });
      });
    });
  }

  location.chillers?.forEach(chiller => {
    const ct = CHILLER_TYPES.find(c => c.id === chiller.typeId);
    const level = ct?.levels.find(l => l.level === chiller.level);
    if (level) overheadPowerKW += level.powerConsumptionKW;
  });

  if (itPowerKW === 0) return 1.0;
  return (itPowerKW + overheadPowerKW) / itPowerKW;
};

/**
 * Calculates aggregate PUE across all unlocked locations, weighted by IT load.
 */
export const calculateAggregatePUE = (locations: Record<string, LocationData>): number => {
  let totalIT = 0;
  let totalFacility = 0;
  Object.values(locations).forEach(location => {
    if (!location.unlocked) return;
    let itKW = 0;
    let overheadKW = 0;
    if (location.floors) {
      Object.values(location.floors).forEach(floor => {
        floor.racks?.forEach(rack => {
          rack.servers?.forEach(server => {
            const st = SERVER_TYPES.find(s => s.id === server.type);
            if (st) itKW += st.powerDraw;
          });
        });
      });
    }
    location.chillers?.forEach(chiller => {
      const ct = CHILLER_TYPES.find(c => c.id === chiller.typeId);
      const level = ct?.levels.find(l => l.level === chiller.level);
      if (level) overheadKW += level.powerConsumptionKW;
    });
    totalIT += itKW;
    totalFacility += itKW + overheadKW;
  });
  if (totalIT === 0) return 1.0;
  return totalFacility / totalIT;
};
